import { FBXReader, FBXReaderNode, parseBinary, parseText } from 'fbx-parser';
import { Vec3 } from 'playcanvas';

// FBX internal time unit: 1 second = 46186158000 ticks
const FBX_TICKS_PER_SECOND = 46186158000;

// detect binary vs ASCII FBX and return a ready-to-query FBXReader
const parseFbxBuffer = (buffer: ArrayBuffer): FBXReader => {
    const bytes = new Uint8Array(buffer);

    // binary FBX files start with the magic header "Kaydara FBX Binary"
    const header = new TextDecoder('ascii').decode(bytes.slice(0, 20));
    const fbxData = header.startsWith('Kaydara FBX Binary') ?
        parseBinary(bytes) :
        parseText(new TextDecoder('utf-8').decode(bytes));

    return new FBXReader(fbxData);
};

// convert a source axis (per FBX GlobalSettings UpAxis) into this app's Y-up convention.
// FBX UpAxis: 0 = X, 1 = Y, 2 = Z. Most Y-up exports (Maya, Blender's FBX exporter) need no
// conversion; Z-up sources are rotated so Z becomes up.
const makeAxisConverter = (upAxis: number, upAxisSign: number) => {
    if (upAxis === 2) {
        // Z-up -> Y-up: (x, y, z) -> (x, z, -y * sign)
        return (v: Vec3) => new Vec3(v.x, v.z, -v.y * upAxisSign);
    }
    // already Y-up (or X-up, which we don't attempt to correct for)
    return (v: Vec3) => v.clone();
};

// read GlobalSettings axis conventions from a parsed FBX and return a position/vector converter
const readAxisConverter = (reader: FBXReader): (v: Vec3) => Vec3 => {
    const globalSettings = reader.node('GlobalSettings');
    const props70 = globalSettings?.node('Properties70');
    const readSetting = (name: string, fallback: number) => {
        const p = props70?.nodes('P', { 0: name })[0];
        return p ? (p.prop(4, 'number') ?? fallback) : fallback;
    };
    const upAxis = readSetting('UpAxis', 1);
    const upAxisSign = readSetting('UpAxisSign', 1);
    return makeAxisConverter(upAxis, upAxisSign);
};

// read a node's numeric id (props are number or bigint depending on magnitude)
const readId = (node: FBXReaderNode): number => Number(node.prop(0, 'bigint') ?? node.prop(0, 'number'));

// KeyTime/KeyValueFloat/Vertices/PolygonVertexIndex/etc arrays parse as number[] or bigint[]
// depending on magnitude (the ascii parser only promotes to bigint outside
// Number.MAX_SAFE_INTEGER), so read the raw prop rather than asserting a specific array type.
const readNumericArray = (node: FBXReaderNode | undefined): number[] | undefined => {
    const raw = node?.prop(0);
    if (!Array.isArray(raw) || raw.length === 0) {
        return undefined;
    }
    return raw.map(v => (typeof v === 'bigint' ? Number(v) : v as number));
};

export { FBX_TICKS_PER_SECOND, parseFbxBuffer, makeAxisConverter, readAxisConverter, readId, readNumericArray };
